import { useEffect, useRef, useState } from 'react';

import type { AccountResponse } from '@shared/contract';

import type { DemoApi } from '../api/client';

/** Poll interval while the balance/history view is mounted (ms). */
export const ACCOUNT_POLL_INTERVAL_MS = 4000;

export interface UseAccountPollResult {
  account: AccountResponse | null;
  loading: boolean;
  error: string | null;
  waitingForDeposit: boolean;
}

/**
 * Polls GET /account while mounted. Keeps last good data on refresh/errors;
 * never stacks overlapping requests.
 */
export function useAccountPoll(
  api: DemoApi,
  customerId: string | undefined,
  options?: { selectedMintId?: number; intervalMs?: number },
): UseAccountPollResult {
  const intervalMs = options?.intervalMs ?? ACCOUNT_POLL_INTERVAL_MS;
  const selectedMintId = options?.selectedMintId;
  const [account, setAccount] = useState<AccountResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const hasData = useRef(false);

  useEffect(() => {
    if (!customerId) {
      setAccount(null);
      setError(null);
      setLoading(false);
      hasData.current = false;
      return;
    }

    let cancelled = false;
    hasData.current = false;
    setAccount(null);

    const tick = async () => {
      if (cancelled || inFlight.current) return;
      inFlight.current = true;
      if (!hasData.current) setLoading(true);
      try {
        const next = await api.getAccount(customerId);
        if (!cancelled) {
          setAccount(next);
          hasData.current = true;
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Account refresh failed');
        }
      } finally {
        inFlight.current = false;
        if (!cancelled) setLoading(false);
      }
    };

    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, intervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [api, customerId, intervalMs]);

  const selectedKey =
    selectedMintId != null ? String(selectedMintId) : undefined;
  const selectedBalance =
    selectedKey && account?.customerBalances?.[selectedKey]
      ? account.customerBalances[selectedKey]
      : undefined;
  const waitingForDeposit =
    !!account &&
    !!selectedKey &&
    (selectedBalance == null || selectedBalance === '0');

  return { account, loading, error, waitingForDeposit };
}
